import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import { tilesForRole } from "../navConfig";

const COLORS = [
  "#152265",
  "#3b82f6",
  "#14b8a6",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#22c55e",
  "#f97316",
];

function dateToIso(date) {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function monthsAgoIso(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return dateToIso(d);
}

function firstDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function lastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function lastMonthRange() {
  const today = new Date();
  const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

  return {
    start: dateToIso(firstDayOfMonth(prevMonth)),
    end: dateToIso(lastDayOfMonth(prevMonth)),
  };
}

function oneMonthBackRange() {
  const today = new Date();
  const start = new Date(today);
  start.setMonth(start.getMonth() - 1);

  return {
    start: dateToIso(start),
    end: dateToIso(today),
  };
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function sumSeries(series) {
  return (Array.isArray(series) ? series : []).reduce((acc, x) => acc + num(x?.count), 0);
}

function mergeSeriesByLabel(a = [], b = []) {
  const map = new Map();

  for (const row of a || []) {
    const label = String(row?.label || "");
    map.set(label, num(row?.count));
  }

  for (const row of b || []) {
    const label = String(row?.label || "");
    map.set(label, num(map.get(label)) + num(row?.count));
  }

  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((x, y) => String(x.label).localeCompare(String(y.label)));
}

function EmptyChart() {
  return (
    <div style={{ padding: 24, textAlign: "center", opacity: 0.7 }}>
      Engin gögn
    </div>
  );
}

function formatLabel(label) {
  return String(label || "");
}

function shortLabel(label, max = 12) {
  const s = formatLabel(label);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function axisLabelLines(label) {
  const s = formatLabel(label).trim();

  const ym = /^(\d{4})-(\d{2})$/.exec(s);
  if (ym) return [ym[2], ym[1]];

  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) return [`${ymd[3]}/${ymd[2]}`, ymd[1]];

  return [shortLabel(s, 10)];
}

function BarChart({ data }) {
  if (!data?.length) return <EmptyChart />;

  const width = 820;
  const height = 320;
  const margin = { top: 20, right: 20, bottom: 92, left: 78 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const max = Math.max(...data.map((x) => num(x.count)), 1);
  const step = plotW / data.length;
  const barW = Math.max(10, step * 0.62);
  const showEvery = data.length > 10 ? Math.ceil(data.length / 6) : 1;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const y = margin.top + plotH - plotH * p;
        const val = Math.round(max * p);
        return (
          <g key={i}>
            <line x1={margin.left} y1={y} x2={width - margin.right} y2={y} stroke="#e5e7eb" />
            <text x={margin.left - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
              {val}
            </text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const count = num(d.count);
        const barH = (count / max) * plotH;
        const x = margin.left + i * step + (step - barW) / 2;
        const y = margin.top + plotH - barH;
        const lines = axisLabelLines(d.label);

        return (
          <g key={`${d.label}-${i}`}>
            <rect x={x} y={y} width={barW} height={barH} rx="4" fill={COLORS[i % COLORS.length]} />

            <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize="10" fill="#374151">
              {count}
            </text>

            {i % showEvery === 0 && (
              <text
                x={x + barW / 2}
                y={height - 30}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {lines.map((line, idx) => (
                  <tspan key={idx} x={x + barW / 2} dy={idx === 0 ? 0 : 12}>
                    {line}
                  </tspan>
                ))}
                <title>{formatLabel(d.label)}</title>
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function LineOrAreaChart({ data, area = false }) {
  if (!data?.length) return <EmptyChart />;

  const width = 820;
  const height = 320;
  const margin = { top: 20, right: 20, bottom: 92, left: 78 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const max = Math.max(...data.map((x) => num(x.count)), 1);
  const showEvery = data.length > 10 ? Math.ceil(data.length / 6) : 1;

  const points = data.map((d, i) => {
    const x = margin.left + (data.length === 1 ? plotW / 2 : (i * plotW) / (data.length - 1));
    const y = margin.top + plotH - (num(d.count) / max) * plotH;
    return { ...d, x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${margin.top + plotH} L ${points[0].x} ${margin.top + plotH} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const y = margin.top + plotH - plotH * p;
        const val = Math.round(max * p);
        return (
          <g key={i}>
            <line x1={margin.left} y1={y} x2={width - margin.right} y2={y} stroke="#e5e7eb" />
            <text x={margin.left - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
              {val}
            </text>
          </g>
        );
      })}

      {area && <path d={areaPath} fill="rgba(21,34,101,0.14)" stroke="none" />}
      <path d={linePath} fill="none" stroke="#152265" strokeWidth="3" />

      {points.map((p, i) => {
        const lines = axisLabelLines(p.label);

        return (
          <g key={`${p.label}-${i}`}>
            <circle cx={p.x} cy={p.y} r="4" fill="#152265" />

            <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="10" fill="#374151">
              {p.count}
            </text>

            {i % showEvery === 0 && (
              <text
                x={p.x}
                y={height - 30}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
              >
                {lines.map((line, idx) => (
                  <tspan key={idx} x={p.x} dy={idx === 0 ? 0 : 12}>
                    {line}
                  </tspan>
                ))}
                <title>{formatLabel(p.label)}</title>
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function DonutChart({ data }) {
  if (!data?.length) return <EmptyChart />;

  const total = data.reduce((sum, x) => sum + num(x.count), 0);
  if (!total) return <EmptyChart />;

  let running = 0;
  const segments = data.map((d, i) => {
    const value = num(d.count);
    const start = (running / total) * 100;
    running += value;
    const end = (running / total) * 100;
    return `${COLORS[i % COLORS.length]} ${start}% ${end}%`;
  });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 16, alignItems: "center" }}>
      <div
        style={{
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: `conic-gradient(${segments.join(", ")})`,
          position: "relative",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 36,
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            color: "#111827",
            textAlign: "center",
            padding: 8,
            fontSize: 14,
          }}
        >
          Samtals
          <br />
          {total}
        </div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {data.map((d, i) => (
          <div key={`${d.label}-${i}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 999,
                background: COLORS[i % COLORS.length],
                flex: "0 0 auto",
              }}
            />
            <span style={{ flex: 1, wordBreak: "break-word" }}>{d.label}</span>
            <strong>{d.count}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  data,
  defaultType = "bar",
  allowedTypes = ["bar", "line", "area", "donut"],
}) {
  const [type, setType] = useState(defaultType);

  useEffect(() => {
    if (!allowedTypes.includes(type)) {
      setType(defaultType);
    }
  }, [type, allowedTypes, defaultType]);

  let chart = null;
  if (type === "bar") chart = <BarChart data={data} />;
  if (type === "line") chart = <LineOrAreaChart data={data} area={false} />;
  if (type === "area") chart = <LineOrAreaChart data={data} area />;
  if (type === "donut") chart = <DonutChart data={data} />;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d7dbe3",
        borderRadius: 14,
        padding: 16,
        boxShadow: "0 8px 24px rgba(0,0,0,.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "start",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{title}</div>
          {subtitle ? <div style={{ opacity: 0.7, marginTop: 4 }}>{subtitle}</div> : null}
        </div>

        <select value={type} onChange={(e) => setType(e.target.value)}>
          {allowedTypes.includes("bar") && <option value="bar">Súlurit</option>}
          {allowedTypes.includes("line") && <option value="line">Línurit</option>}
          {allowedTypes.includes("area") && <option value="area">Svæðirit</option>}
          {allowedTypes.includes("donut") && <option value="donut">Hringrit</option>}
        </select>
      </div>

      {chart}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        border: "2px solid #222",
        borderRadius: 8,
        padding: "10px 14px",
        textAlign: "center",
        background: "#fff",
        minWidth: 160,
      }}
    >
      <div style={{ fontWeight: 600, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function Statistics() {
  const [role, setRole] = useState(null);
  const tiles = useMemo(() => tilesForRole(role), [role]);

  const [start, setStart] = useState(monthsAgoIso(12));
  const [end, setEnd] = useState(dateToIso(new Date()));
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const canView = role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", { headers: authHeaders() });
        setRole(data?.role || "staff");
      } catch {
        setRole("staff");
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/statistics/", {
        params: {
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
        },
        headers: authHeaders(),
      });
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (role && !canView) {
    return (
      <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
        <h2 style={{ textAlign: "center", marginTop: 20 }}>Aðgangur óheimill</h2>
        <div style={{ textAlign: "center", marginTop: 8, opacity: 0.8 }}>
          Þessi síða er aðeins aðgengileg stjórnendum.
        </div>
      </Shell>
    );
  }

  const summary = stats?.summary || {};
  const series = stats?.series || {};
  const breakdowns = stats?.breakdowns || {};

  const attendanceGroupSeries = Array.isArray(series.attendance_group) ? series.attendance_group : [];
  const attendanceHouseSeries = Array.isArray(series.attendance_house) ? series.attendance_house : [];
  const attendanceTotalSeries =
    Array.isArray(series.attendance_total) && series.attendance_total.length
      ? series.attendance_total
      : mergeSeriesByLabel(attendanceGroupSeries, attendanceHouseSeries);

  const attendanceSplit =
    Array.isArray(breakdowns.attendance_split) && breakdowns.attendance_split.length
      ? breakdowns.attendance_split
      : [
          { label: "Hópamæting", count: num(summary.group_attendance_total ?? sumSeries(attendanceGroupSeries)) },
          { label: "Húsmæting", count: num(summary.house_attendance_total ?? sumSeries(attendanceHouseSeries)) },
        ].filter((x) => x.count > 0);

  const startedAttendanceSplit =
    Array.isArray(breakdowns.started_members_attendance_split) && breakdowns.started_members_attendance_split.length
      ? breakdowns.started_members_attendance_split
      : [
          { label: "Hópamæting", count: num(summary.started_members_group_attendance_total) },
          { label: "Húsmæting", count: num(summary.started_members_house_attendance_total) },
        ].filter((x) => x.count > 0);

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu menuItems={tiles}>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Tölfræði</h2>

      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "end",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 4 }}>Frá</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>

          <div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 4 }}>Til</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>

          <button className="btn sm" onClick={load}>Uppfæra</button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className="btn ghost sm"
            onClick={() => {
              const r = lastMonthRange();
              setStart(r.start);
              setEnd(r.end);
            }}
          >
            Síðasti mánuður
          </button>

          <button
            className="btn ghost sm"
            onClick={() => {
              const r = oneMonthBackRange();
              setStart(r.start);
              setEnd(r.end);
            }}
          >
            Síðasti 1 mánuður
          </button>

          <button
            className="btn ghost sm"
            onClick={() => {
              setStart(monthsAgoIso(3));
              setEnd(dateToIso(new Date()));
            }}
          >
            Síðustu 3 mán.
          </button>

          <button
            className="btn ghost sm"
            onClick={() => {
              setStart(monthsAgoIso(6));
              setEnd(dateToIso(new Date()));
            }}
          >
            Síðustu 6 mán.
          </button>

          <button
            className="btn ghost sm"
            onClick={() => {
              setStart(monthsAgoIso(12));
              setEnd(dateToIso(new Date()));
            }}
          >
            Síðustu 12 mán.
          </button>

          <button
            className="btn ghost sm"
            onClick={() => {
              setStart("");
              setEnd("");
            }}
          >
            Allt
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <StatCard label="Félagsaðilar" value={summary.members_total ?? 0} />
            <StatCard label="Virkir" value={summary.members_active ?? 0} />
            <StatCard label="Óvirkir" value={summary.members_disabled ?? 0} />
            <StatCard label="Í endurhæfingu" value={summary.members_in_rehab ?? 0} />
            <StatCard label="Í úrræði" value={summary.members_in_urraedi ?? 0} />
            <StatCard label="Teamsarar" value={summary.members_in_teamsari ?? 0} />
            <StatCard label="Jafningjastuðn." value={summary.members_peer_support ?? 0} />
            <StatCard label="Hópstjórar" value={summary.members_group_leaders ?? 0} />
            <StatCard label="Sjálfboðaliðar" value={summary.members_volunteers ?? 0} />
            <StatCard label="Hópar" value={summary.groups_total ?? 0} />
            <StatCard label="Virkir hópar" value={summary.groups_active ?? 0} />
            <StatCard label="Óvirkir hópar" value={summary.groups_inactive ?? 0} />
            <StatCard label="Mæting samtals" value={summary.attendance_total ?? sumSeries(attendanceTotalSeries)} />
            <StatCard label="Hópamæting" value={summary.group_attendance_total ?? sumSeries(attendanceGroupSeries)} />
            <StatCard label="Húsmæting" value={summary.house_attendance_total ?? sumSeries(attendanceHouseSeries)} />
            <StatCard label="Einstakir mætingaraðilar" value={summary.attendance_unique_users ?? 0} />
            <StatCard label="Meðaltal mætinga á mann" value={summary.attendance_avg_per_user ?? 0} />
            <StatCard label="Byrjuðu á tímabili" value={summary.started_members_total ?? 0} />
            <StatCard label="Mæting nýrra fél." value={summary.started_members_attendance_total ?? 0} />
            <StatCard label="Meðaltal mætinga nýrra fél." value={summary.started_members_attendance_avg ?? 0} />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 16,
              maxWidth: 1100,
              margin: "0 auto",
            }}
          >
            <ChartCard
              title="Nýir félagsaðilar"
              subtitle="Fjöldi skráðra félagsaðila yfir tíma"
              data={series.members_created || []}
              defaultType="line"
              allowedTypes={["line", "bar", "area"]}
            />

            <ChartCard
              title="Byrjuðu í Hugarafli"
              subtitle="Byrjun í Hugarafli yfir tíma"
              data={series.members_started || []}
              defaultType="bar"
              allowedTypes={["bar", "line", "area"]}
            />

            <ChartCard
              title="Mæting samtals yfir tíma"
              subtitle="Öll hópa- og húsmæting á völdu tímabili"
              data={attendanceTotalSeries}
              defaultType="line"
              allowedTypes={["line", "bar", "area"]}
            />

            <ChartCard
              title="Hópamæting yfir tíma"
              subtitle="Fjöldi mætinga í hópa yfir tíma"
              data={attendanceGroupSeries}
              defaultType="bar"
              allowedTypes={["bar", "line", "area"]}
            />

            <ChartCard
              title="Húsmæting yfir tíma"
              subtitle="Fjöldi mætinga í hús yfir tíma"
              data={attendanceHouseSeries}
              defaultType="bar"
              allowedTypes={["bar", "line", "area"]}
            />

            <ChartCard
              title="Skipting mætinga"
              subtitle="Hópamæting samanborið við húsmætingu"
              data={attendanceSplit}
              defaultType="donut"
              allowedTypes={["donut", "bar"]}
            />

            <ChartCard
              title="Mæting hjá þeim sem byrjuðu á tímabilinu"
              subtitle="Hversu oft fólk sem byrjaði á völdu tímabili mætti í hópa"
              data={series.started_members_group_attendance || []}
              defaultType="bar"
              allowedTypes={["bar", "line", "area"]}
            />

            <ChartCard
              title="Húsmæting hjá þeim sem byrjuðu á tímabilinu"
              subtitle="Hversu oft fólk sem byrjaði á völdu tímabili mætti í hús"
              data={series.started_members_house_attendance || []}
              defaultType="bar"
              allowedTypes={["bar", "line", "area"]}
            />

            <ChartCard
              title="Skipting mætinga hjá nýjum félagsaðilum"
              subtitle="Hópamæting samanborið við húsmætingu hjá þeim sem byrjuðu á tímabilinu"
              data={startedAttendanceSplit}
              defaultType="donut"
              allowedTypes={["donut", "bar"]}
            />

            <ChartCard
              title="Staða félagsaðila"
              subtitle="Virkni, endurhæfing, úrræði og Teamsarar"
              data={breakdowns.member_status || []}
              defaultType="donut"
              allowedTypes={["donut", "bar"]}
            />

            <ChartCard
              title="Hlutverk félagsaðila"
              subtitle="Starfsfólk, hópstjórar, jafningjastuðningur og sjálfboðaliðar"
              data={breakdowns.member_roles || []}
              defaultType="bar"
              allowedTypes={["bar", "donut"]}
            />

            <ChartCard
              title="Staða hópa"
              subtitle="Virkir og óvirkir hópar"
              data={breakdowns.group_status || []}
              defaultType="donut"
              allowedTypes={["donut", "bar"]}
            />

            <ChartCard
              title="Mæting og endurhæfing"
              subtitle="Fjöldi mætinga hjá einstaklingum í og utan endurhæfingar"
              data={breakdowns.attendance_rehab_split || []}
              defaultType="bar"
              allowedTypes={["bar", "donut"]}
            />
          </div>
        </>
      )}
    </Shell>
  );
}